import React, { useState } from 'react';
import { trackEvent, BOOKING_SCREENING_URL, PHONE_NUMBER, PHONE_TEL } from '../lib/tracking';
import { optimizeImage } from '../lib/image';
import TrustBar from '../components/TrustBar';

export default function AboutKim() {
  const [selectedImage, setSelectedImage] = useState(null);

  const educations = [
    {
      title: 'Hypnoterapeut',
      institution: 'Dansk NLP Institut',
      year: 'Marts 2026',
    },
    {
      title: 'Aktionsforskningsuddannelse',
      institution: 'Dansk Psykoterapeutforening',
      year: 'December 2025',
    },
    {
      title: 'Transbiologisk Traume Terapi (TBT)',
      institution: 'Skolen for Traumeforløsende Psykoterapi',
      year: 'Februar 2025',
    },
    {
      title: 'Supervisoruddannelse',
      institution: 'Dansk NLP Institut',
      year: 'Februar 2017',
    },
    {
      title: 'Klientvejledning og klientbehandling',
      institution: 'Jette Hastrup',
      year: 'Juni 2016',
    },
    {
      title: 'Psykoterapeut (4-årig uddannelse)',
      institution: 'Dansk NLP Institut',
      year: 'Januar 2016',
    },
    {
      title: 'Socialpsykologi, Neuropsykologi & Personlighedspsykologi',
      institution: 'Folkeuniversitetet',
      year: 'Løbende efteruddannelse',
    },
  ];

  const galleryImages = [
    {
      url: 'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/profile-img.png',
      caption: 'Kim Palasz – Psykoterapeut MPF',
    },
    {
      url: 'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/img-8587.jpeg',
      caption: 'Nærværende samtale i trygge rammer på Amager',
    },
    {
      url: 'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/img-1647.jpeg',
      caption: 'Anerkendende og ufordømmende tilgang',
    },
    {
      url: 'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/img-4513.jpg',
      caption: 'Roligt samtalerum på Marengovej 2',
    },
    {
      url: 'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/img-4515.jpg',
      caption: 'Plads til fordybelse og heling',
    },
    {
      url: 'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/img-4518.jpg',
      caption: 'Behagelige rammer til samtale og kropslig ro',
    },
  ];

  return (
    <div className="bg-[#ECEEF1]">
      {/* Hero */}
      <section className="py-16 sm:py-24 bg-white border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#5A232E]/10 text-[#5A232E] text-xs font-semibold tracking-wide">
                <i className="fa-solid fa-award text-[#5A232E]" aria-hidden="true" />
                <span>Medlem af Dansk Psykoterapeutforening (MPF)</span>
              </div>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#5A232E] tracking-tight leading-[1.12]">
                Mød Kim Palasz
              </h1>

              <p className="text-base sm:text-lg text-[#6c7480] leading-relaxed">
                Psykoterapeut MPF, hypnoterapeut, traumeterapeut, supervisor og coach. Siden 2014 har jeg hjulpet mere end 500 mennesker med at genfinde roen, tackle modgang og skabe et liv i balance.
              </p>

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <a
                  href={BOOKING_SCREENING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('cta_booking_click', { location: 'about_kim_hero' })}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-sm shadow-md transition-all no-break whitespace-nowrap"
                >
                  <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                  <span>Book en gratis afklaringssamtale</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-[#ECEEF1] bg-[#D9DDE2]">
                <img
                  src={optimizeImage(
                    'kim-portrait-vertical.webp',
                    900
                  )}
                  alt="Kim Palasz Psykoterapeut MPF"
                  className="w-full h-auto object-cover max-h-[500px]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <TrustBar />

      {/* Personlig introduktion & tilgang */}
      <section className="py-20 sm:py-24 bg-[#ECEEF1] border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-8 sm:p-12 border border-[#D9DDE2] shadow-xs space-y-6 text-[#11161d] leading-relaxed">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block">
              Min baggrund
            </span>
            <h2 className="font-display text-3xl text-[#5A232E]">
              Hvem er jeg som menneske og terapeut?
            </h2>

            <p>
              Jeg er fra 1976 og er født og opvokset i Næstved, men har boet i København siden 1995. I dag bor jeg ved Lergravsparken på Amager, hvor jeg også har min praksis.
            </p>
            <p>
              Siden 2014 har jeg drevet min egen praksis, hvor jeg tilbyder terapi til klienter, der ønsker personlig udvikling, øget trivsel og redskaber til at håndtere livets udfordringer. Jeg har løbende haft klienter i individuelle 1-1 samtaler og i supervisions- og gruppeforløb.
            </p>
            <p>
              Mit arbejde er baseret på en nærværende og anerkendende tilgang, hvor vi sammen finder dine ressourcer og styrker. Jeg tror på, at du allerede har de nødvendige værktøjer i dig – min opgave er at hjælpe dig med at aktivere dem.
            </p>
            <p>
              Min mission er at skabe et trygt rum, hvor du kan vokse, udvikle dig og finde balance i livet.
            </p>
            <p>
              Udover min praksis er jeg underviser på psykoterapeutuddannelsen på <strong>Dansk NLP Institut</strong>, og har undervist ledere og medarbejdere inden for kommunikation, coaching og personlig udvikling.
            </p>

            <div className="pt-6 border-t border-[#D9DDE2] grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center gap-4 p-4 rounded-2xl bg-[#ECEEF1]/60 border border-[#D9DDE2]">
                <img
                  src={optimizeImage(
                    'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/jpg-black.webp',
                    140
                  )}
                  alt="Psykoterapeutforeningen MPF"
                  className="h-14 w-auto object-contain shrink-0"
                />
                <div className="text-xs text-[#6c7480]">
                  <strong className="text-[#5A232E] block text-sm">Godkendt af Dansk Psykoterapeutforening (MPF)</strong>
                  Sikrer etisk regelsæt, tavshedspligt og godkendt 4-årig psykoterapeutuddannelse.
                </div>
              </div>

              <a
                href="https://nghdanmark.dk/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 rounded-2xl bg-[#ECEEF1]/60 border border-[#D9DDE2] hover:border-[#5A232E]/40 hover:bg-[#ECEEF1] transition-all group"
              >
                <img
                  src={optimizeImage('danske-hypnoterapeuter.png', 140)}
                  alt="Medlem af Danske Hypnoterapeuter"
                  className="h-14 w-auto object-contain shrink-0 group-hover:scale-105 transition-transform"
                />
                <div className="text-xs text-[#6c7480]">
                  <strong className="text-[#5A232E] block text-sm flex items-center gap-1.5">
                    Medlem af Danske Hypnoterapeuter
                    <i className="fa-solid fa-arrow-up-right-from-square text-[10px] text-[#5A232E]/60 group-hover:text-[#5A232E]" aria-hidden="true" />
                  </strong>
                  Certificeret hypnoterapeut og medlem af NGH Danmark / Danske Hypnoterapeuter.
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Uddannelser & Faglighed */}
      <section className="py-20 sm:py-24 bg-white border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block mb-2">
              Faglighed & Kvalitet
            </span>
            <h2 className="font-display text-3xl sm:text-4xl text-[#5A232E]">
              Mine uddannelser og kvalifikationer
            </h2>
            <p className="text-sm text-[#6c7480] mt-3">
              Løbende supervision og efteruddannelse sikrer højeste faglige og etiske standard.
            </p>
          </div>

          <div className="space-y-4">
            {educations.map((edu, idx) => (
              <div
                key={idx}
                className="bg-[#ECEEF1] p-5 sm:p-6 rounded-2xl border border-[#D9DDE2] flex flex-col sm:flex-row sm:items-center justify-between gap-2"
              >
                <div>
                  <h3 className="font-display text-lg text-[#5A232E] font-medium">
                    {edu.title}
                  </h3>
                  <div className="text-xs sm:text-sm text-[#6c7480]">{edu.institution}</div>
                </div>
                <div className="text-xs font-semibold text-[#5A232E] uppercase tracking-wider bg-white px-3 py-1 rounded-full border border-[#D9DDE2] self-start sm:self-center shrink-0">
                  {edu.year}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Billedgalleri: Praksis og konsultation på Amager */}
      <section className="py-20 sm:py-24 bg-[#ECEEF1] border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block mb-2">
              Lokalerne på Amager
            </span>
            <h2 className="font-display text-3xl sm:text-4xl text-[#5A232E]">
              Et trygt og roligt samtalerum
            </h2>
            <p className="text-sm text-[#6c7480] mt-2">
              Min praksis ligger på Marengovej 2, 2300 København S, få minutters gang fra Lergravsparken Metro. Klik på et billede for at se i stort format.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((img, i) => (
              <div
                key={i}
                onClick={() => setSelectedImage(img)}
                className="group relative rounded-2xl overflow-hidden shadow-xs hover:shadow-xl transition-all cursor-pointer bg-black/10 aspect-4/3"
              >
                <img
                  src={optimizeImage(img.url, 600)}
                  alt={img.caption}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                  <span className="text-xs text-white font-medium flex items-center gap-2">
                    <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true" />
                    <span>{img.caption}</span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox modal for gallery images */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-xs animate-in fade-in"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="relative max-w-4xl max-h-[90vh] bg-white rounded-2xl overflow-hidden p-2"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black transition-colors"
            >
              <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
            </button>
            <img
              src={selectedImage.url}
              alt={selectedImage.caption}
              className="w-full max-h-[80vh] object-contain rounded-xl"
            />
            <div className="p-3 text-center text-xs text-[#6c7480]">
              {selectedImage.caption}
            </div>
          </div>
        </div>
      )}

      {/* Afsluttende booking CTA */}
      <section className="py-20 bg-[#5A232E] text-white text-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <h2 className="font-display text-3xl sm:text-4xl text-white">
            Vil du høre, om jeg er den rette for dig?
          </h2>
          <p className="text-sm sm:text-base text-white/80 max-w-xl mx-auto leading-relaxed">
            Du er altid velkommen til at række ud. Book en gratis afklaringssamtale (20 min.), hvor vi taler om dine ønsker.
          </p>
          <a
            href={BOOKING_SCREENING_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent('cta_booking_click', { location: 'about_kim_bottom' })}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-white hover:bg-[#ECEEF1] text-[#5A232E] font-semibold text-base shadow-xl transition-all"
          >
            <i className="fa-solid fa-calendar-check" aria-hidden="true" />
            <span>Book en gratis afklaringssamtale</span>
          </a>
        </div>
      </section>
    </div>
  );
}
